import { useScrollReveal } from '../../hooks/useScrollReveal';
import { PRODUCT_IMAGES } from '../../data/mock';
import { AlertCircle, TrendingUp, Lightbulb } from 'lucide-react';

const EYE_IMAGE = "https://customer-assets.emergentagent.com/job_rosy-sales-landing/artifacts/10v2j6gm_image.png";

export const ProblemSection = () => {
  const [ref1, vis1] = useScrollReveal();
  const [ref2, vis2] = useScrollReveal();
  const [ref3, vis3] = useScrollReveal();

  return (
    <>
      {/* Pain Points */}
      <section id="problema" className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(252,231,243,0.8) 0%, rgba(253,242,248,0.6) 50%, rgba(252,231,243,0.7) 100%)' }}>
        <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #FBCFE8 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #F9A8D4 0%, transparent 70%)' }} />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div ref={ref1} className={`reveal ${vis1 ? 'visible' : ''}`}>
            <div className="max-w-3xl mb-16">
              <p className="font-body text-xl leading-relaxed mb-3" style={{ color: '#3D2E50' }}>Has aprendido técnicas.</p>
              <p className="font-body text-xl leading-relaxed" style={{ color: '#3D2E50' }}>Has practicado una y otra vez.</p>
            </div>
          </div>

          {/* Pain point cards */}
          <div ref={ref2} className={`stagger-children ${vis2 ? 'visible' : ''} grid md:grid-cols-2 gap-5 mb-16`}>
            {[
              'Empiezas un diseño y dudas si la estructura es la correcta.',
              'Si el arco realmente favorece el rostro.',
              'Si el grosor está bien equilibrado.',
              'Si ese pequeño ajuste que hiciste fue el indicado.'
            ].map((text, i) => (
              <div key={i} className="glass-strong rounded-2xl p-6 hover-glow flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#DB2777' }}>
                  <AlertCircle size={20} />
                </div>
                <p className="font-body text-base leading-relaxed" style={{ color: '#3D2E50' }}>{text}</p>
              </div>
            ))}
          </div>

          <div ref={ref3} className={`reveal ${vis3 ? 'visible' : ''}`}>
            <div className="grid lg:grid-cols-5 gap-10 items-center">
              {/* Eye image on the left */}
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-full max-w-xs overflow-hidden rounded-3xl shadow-xl" style={{ boxShadow: '0 30px 60px -15px rgba(190, 24, 93, 0.2)' }}>
                  <img src={EYE_IMAGE} alt="Diseño profesional de cejas" className="w-full h-auto object-cover" />
                </div>
              </div>
              {/* Text on the right */}
              <div className="lg:col-span-3 space-y-6">
                <p className="font-body text-lg leading-relaxed" style={{ color: '#3D2E50' }}>
                  Y aunque la clienta no diga nada negativo,
                  por dentro te preguntas si realmente volverá.
                  Si te elegirá otra vez.
                </p>
                <div className="glass-pink rounded-2xl p-6">
                  <p className="font-display text-xl italic leading-relaxed" style={{ color: '#9D174D' }}>
                    Esa sensación no es falta de talento.
                    Es que no siempre te sientes completamente segura mientras las diseñas.
                    Y cuando no puedes confirmar lo que estás haciendo,
                    la duda empieza a acompañarte.
                  </p>
                </div>
                <p className="font-body text-lg font-semibold" style={{ color: '#831843' }}>
                  No es que no sepas hacerlo.<br />
                  Es que aún no tienes una base clara que sostenga cada decisión.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Practice Alone Isn't Enough */}
      <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #FDF2F8 0%, #FCE7F3 40%, #FBCFE8 70%, #FCE7F3 100%)' }}>
        <div className="absolute top-20 right-10 w-[300px] h-[300px] rounded-full blob opacity-15" style={{ background: 'radial-gradient(circle, #F472B6 0%, transparent 70%)' }} />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className={`space-y-8`}>
              <div className="glass-strong rounded-2xl p-8 shimmer-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#BE185D' }}>
                    <TrendingUp size={16} />
                  </div>
                  <span className="font-body text-sm font-bold uppercase tracking-wider" style={{ color: '#BE185D' }}>El problema real</span>
                </div>
                <p className="font-body text-base leading-relaxed" style={{ color: '#3D2E50' }}>
                  Es fácil pensar que lo que necesitas es más práctica.
                  Más horas intentando que el diseño "salga mejor".
                  Repetir estructuras que viste en una clase.
                  Pero la práctica, por sí sola, no corrige la duda.
                  <span className="font-semibold" style={{ color: '#BE185D' }}> Solo la repite.</span>
                </p>
              </div>

              <div className="glass-strong rounded-2xl p-8 shimmer-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(252,231,243,0.9)', color: '#DB2777' }}>
                    <Lightbulb size={16} />
                  </div>
                  <span className="font-body text-sm font-bold uppercase tracking-wider" style={{ color: '#DB2777' }}>La verdadera clave</span>
                </div>
                <p className="font-body text-base leading-relaxed" style={{ color: '#3D2E50' }}>
                  Porque el diseño no se vuelve seguro con repetición.
                  <span className="font-semibold" style={{ color: '#BE185D' }}> Se vuelve seguro cuando entiendes la lógica detrás de cada trazo.</span>
                </p>
                <div className="mt-4 space-y-2">
                  {['Cuando sabes por qué una línea va ahí.', 'Por qué ese arco se eleva.', 'Por qué ese grosor cambia la expresión.'].map((t, i) => (
                    <p key={i} className="font-body text-base" style={{ color: '#4A3B5C' }}>{t}</p>
                  ))}
                </div>
              </div>

              <div className="glass-pink rounded-2xl p-6">
                <p className="font-body text-base mb-2" style={{ color: '#3D2E50' }}>
                  Ahí deja de ser prueba y error. <span className="font-bold" style={{ color: '#9D174D' }}>Se convierte en criterio.</span>
                </p>
                <p className="font-body text-base" style={{ color: '#3D2E50' }}>
                  Y cuando tienes criterio, ya no trabajas desde la duda. <span className="font-bold" style={{ color: '#9D174D' }}>Trabajas desde la decisión consciente.</span>
                </p>
              </div>

              <p className="font-display text-xl italic" style={{ color: '#831843' }}>
                Y esa diferencia no es pequeña.<br />
                Es lo que separa intentar... de diseñar con certeza profesional.
              </p>
            </div>

            {/* Product image - clean, no floating extras */}
            <div className="flex justify-center">
              <div className="perspective-container">
                <div className="product-3d-reverse relative">
                  <img src={PRODUCT_IMAGES.secondary} alt="Manual Cejas Radiantes" className="w-full max-w-sm rounded-2xl" style={{ objectFit: 'cover' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sistema de Diseño con Certeza */}
      <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, rgba(252,231,243,0.6) 0%, rgba(253,242,248,0.8) 50%, rgba(251,207,232,0.5) 100%)' }}>
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #F472B6 0%, transparent 70%)' }} />

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center space-y-8">
            {/* Concept cards */}
            <div className="grid sm:grid-cols-3 gap-5 mb-10">
              {[
                { title: 'La forma', desc: 'deja de ser una medida suelta.' },
                { title: 'El arco', desc: 'deja de ser una intuición.' },
                { title: 'El grosor', desc: 'deja de ser una decisión al azar.' }
              ].map((item, i) => (
                <div key={i} className="glass-strong rounded-2xl p-6 hover-lift text-center">
                  <p className="font-display text-lg font-bold mb-2" style={{ color: '#BE185D' }}>{item.title}</p>
                  <p className="font-body text-sm" style={{ color: '#4A3B5C' }}>{item.desc}</p>
                </div>
              ))}
            </div>

            <p className="font-body text-lg" style={{ color: '#3D2E50' }}>
              Cuando el diseño se entiende como un conjunto de pasos aislados, cada parte se siente incierta.
              Pero cuando lo ves como una estructura completa, todo empieza a conectarse.
            </p>

            <p className="font-body text-lg" style={{ color: '#3D2E50' }}>
              Cada elemento responde a una lógica. Y cuando comprendes esa lógica, ya no necesitas adivinar.
            </p>

            <p className="font-display text-3xl font-bold gradient-text py-2">Puedes confirmar.</p>

            <div className="pt-4">
              <p className="font-body text-base mb-2" style={{ color: '#7E6E91' }}>A ese enfoque lo llamo:</p>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold py-4 gradient-text">
                Sistema de Diseño con Certeza.
              </h2>
              <p className="font-body text-lg max-w-2xl mx-auto" style={{ color: '#3D2E50' }}>
                Un sistema que organiza el diseño de cejas de forma integral,
                para que cada decisión tenga fundamento
                y cada resultado sea consecuencia de lo que entendiste, no de lo que improvisaste.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
